import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '@/layouts/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useAuthState } from '@/hooks/useAuthState';
import { useToast } from '@/hooks/use-toast';
import { getDevelopmentLogoUrl } from '@/config/api';
import { getPlanMeta, normalizePlanSlug } from '@/lib/subscriptionPlanMeta';
import { cn } from '@/lib/utils';
import {
  ResortService,
  type ResortsDashboardStats,
  type ResortStat,
  type ResortPlanCount,
} from '@/services/resortService';
import {
  Palmtree,
  Building2,
  Home,
  Users,
  TrendingUp,
  Loader2,
  MapPin,
  CalendarDays,
  Crown,
  User,
} from 'lucide-react';

function OwnersByPlanBars({
  items,
  total,
}: {
  items: ResortPlanCount[];
  total: number;
}) {
  if (items.length === 0) {
    return <p className="text-sm text-muted-foreground">Nenhum proprietário</p>;
  }

  return (
    <div className="space-y-2">
      {items.map((item) => {
        const slug = normalizePlanSlug(item.planSlug, item.planId);
        const meta = getPlanMeta(item.planSlug, item.planId);
        return (
          <div
            key={item.planId}
            className={cn(
              'flex w-full items-center justify-between gap-3 rounded-lg px-3 py-2',
              'whitespace-nowrap',
              meta.badgeClassName,
            )}
          >
            <span className="flex min-w-0 items-center gap-1.5 text-sm font-semibold">
              {slug === 'live' && <Crown className="h-3.5 w-3.5 shrink-0" aria-hidden />}
              {slug === 'porto2life' && (
                <Building2 className="h-3.5 w-3.5 shrink-0" aria-hidden />
              )}
              {slug === 'freemium' && <User className="h-3.5 w-3.5 shrink-0" aria-hidden />}
              <span className="truncate">{meta.shortLabel}</span>
            </span>
            <span className="shrink-0 text-base font-bold tabular-nums">{item.ownerCount}</span>
          </div>
        );
      })}
      {total > 0 && (
        <div className="flex w-full items-center justify-between gap-3 rounded-lg border bg-muted/50 px-3 py-2 whitespace-nowrap">
          <span className="text-xs font-semibold text-muted-foreground">Total</span>
          <span className="shrink-0 text-base font-bold tabular-nums">{total}</span>
        </div>
      )}
    </div>
  );
}

function formatCurrency(value: number | null | undefined): string {
  if (value == null || value <= 0) return '—';
  return `R$ ${value.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function ResortCard({ resort }: { resort: ResortStat }) {
  const location = [resort.city, resort.state].filter(Boolean).join(' / ');
  const imagePath =
    (typeof resort.coverImage === 'string' && resort.coverImage) ||
    (typeof resort.logo === 'string' && resort.logo) ||
    null;
  const imageUrl = imagePath ? getDevelopmentLogoUrl(imagePath) : null;

  return (
    <Card className={resort.isActive ? '' : 'opacity-80'}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            {imageUrl ? (
              <img
                src={imageUrl}
                alt={resort.name}
                className="h-12 w-12 rounded-lg object-cover border shrink-0"
              />
            ) : (
              <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                <Building2 className="h-6 w-6 text-primary" />
              </div>
            )}
            <div className="min-w-0">
              <CardTitle className="text-lg truncate">{resort.name}</CardTitle>
              {location && (
                <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                  <MapPin className="h-3 w-3 shrink-0" />
                  <span className="truncate">{location}</span>
                </p>
              )}
            </div>
          </div>
          <Badge variant={resort.isActive ? 'default' : 'secondary'}>
            {resort.isActive ? 'Ativo' : 'Inativo'}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-3 gap-3">
          <div className="rounded-lg border bg-muted/30 p-3 text-center">
            <Home className="h-4 w-4 mx-auto mb-1 text-muted-foreground" />
            <p className="text-xl font-bold">{resort.propertyCount}</p>
            <p className="text-xs text-muted-foreground">imóveis</p>
          </div>
          <div className="rounded-lg border bg-muted/30 p-3 text-center">
            <Users className="h-4 w-4 mx-auto mb-1 text-muted-foreground" />
            <p className="text-xl font-bold">{resort.ownerCount}</p>
            <p className="text-xs text-muted-foreground">proprietários</p>
          </div>
          <div className="rounded-lg border bg-muted/30 p-3 text-center">
            <TrendingUp className="h-4 w-4 mx-auto mb-1 text-muted-foreground" />
            <p className="text-sm font-bold leading-tight">
              {formatCurrency(resort.avgPricePerNight)}
            </p>
            <p className="text-xs text-muted-foreground">média diária</p>
          </div>
        </div>

        {resort.minPricePerNight != null && resort.maxPricePerNight != null && (
          <p className="text-xs text-muted-foreground text-center">
            Faixa: {formatCurrency(resort.minPricePerNight)} —{' '}
            {formatCurrency(resort.maxPricePerNight)}
            {resort.availabilityPeriodsCount > 0 && (
              <span className="block mt-0.5">
                {resort.availabilityPeriodsCount} períodos com preço ativos
              </span>
            )}
          </p>
        )}

        <div>
          <p className="text-sm font-medium mb-2">Proprietários por plano</p>
          <OwnersByPlanBars
            items={
              resort.ownersByPlan ??
              (() => {
                const map = new Map<number, ResortPlanCount>();
                for (const o of resort.owners) {
                  const pid = o.planId || 1;
                  const cur = map.get(pid);
                  if (cur) cur.ownerCount += 1;
                  else
                    map.set(pid, {
                      planId: pid,
                      planSlug: o.subscriptionPlan,
                      planName: o.planName,
                      ownerCount: 1,
                    });
                }
                return Array.from(map.values()).sort((a, b) => a.planId - b.planId);
              })()
            }
            total={resort.ownerCount}
          />
        </div>

        {resort.ownerCount > 0 ? (
          <Button variant="outline" size="sm" className="w-full" asChild>
            <Link
              to={`/admin/usuarios?tipo=owner&developmentId=${resort.developmentId}&resort=${encodeURIComponent(resort.name)}`}
            >
              <Users className="h-4 w-4 mr-2" />
              Ver {resort.ownerCount}{' '}
              {resort.ownerCount === 1 ? 'proprietário' : 'proprietários'} em Usuários
            </Link>
          </Button>
        ) : (
          <p className="text-sm text-muted-foreground text-center">Nenhum proprietário vinculado</p>
        )}
      </CardContent>
    </Card>
  );
}

const AdminResorts = () => {
  const { isAuthenticated, userType } = useAuthState();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<ResortsDashboardStats | null>(null);

  useEffect(() => {
    if (isAuthenticated && userType === 'admin') {
      loadStats();
    }
  }, [isAuthenticated, userType]);

  const loadStats = async () => {
    try {
      setLoading(true);
      const data = await ResortService.getDashboardStats();
      setStats(data);
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : 'Erro ao carregar dashboard de resorts';
      toast({
        title: 'Erro',
        description: message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </AdminLayout>
    );
  }

  if (!stats) {
    return (
      <AdminLayout>
        <Card>
          <CardContent className="p-6">
            <p className="text-muted-foreground">Nenhum dado disponível.</p>
          </CardContent>
        </Card>
      </AdminLayout>
    );
  }

  const { summary, resorts, unassigned } = stats;

  return (
    <AdminLayout>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="mb-6 sm:mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold mb-2 flex items-center gap-2">
              <Palmtree className="h-7 w-7 text-primary" />
              Resorts
            </h1>
            <p className="text-sm sm:text-base text-muted-foreground">
              Dashboard dos empreendimentos: imóveis, proprietários e média de diária por resort
            </p>
          </div>
          <Button variant="outline" asChild>
            <Link to="/admin/empreendimentos">
              <Building2 className="h-4 w-4 mr-2" />
              Lista de empreendimentos
            </Link>
          </Button>
        </div>

        <Card className="mb-6 overflow-hidden border-0 shadow-md">
          <CardContent className="p-0">
            <Link
              to="/admin/usuarios?tipo=owner&plano=live"
              className={cn(
                'flex w-full items-center justify-between gap-4 px-5 py-5 sm:px-6 sm:py-6',
                'bg-gradient-to-r from-orange-500 to-amber-500 text-white',
                'hover:from-orange-600 hover:to-amber-600 transition-colors',
              )}
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-white/20">
                  <Crown className="h-6 w-6" aria-hidden />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-white/90">Plano Live — visão geral</p>
                  <p className="text-xs text-white/80">Proprietários com assinatura Live na plataforma</p>
                </div>
              </div>
              <div className="text-right shrink-0">
                <p className="text-4xl font-bold tabular-nums leading-none">
                  {summary.livePlanOwnersCount ?? 0}
                </p>
                <p className="text-xs text-white/90 mt-1">
                  {(summary.livePlanOwnersCount ?? 0) === 1 ? 'proprietário' : 'proprietários'}
                </p>
              </div>
            </Link>
          </CardContent>
        </Card>

        {/* Resumo geral */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <Palmtree className="h-4 w-4" />
                <span className="text-xs">Resorts</span>
              </div>
              <p className="text-2xl font-bold">{summary.totalDevelopments}</p>
              <p className="text-xs text-muted-foreground">
                {summary.activeDevelopments} ativos
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <Home className="h-4 w-4" />
                <span className="text-xs">Imóveis</span>
              </div>
              <p className="text-2xl font-bold">{summary.totalProperties}</p>
              {summary.propertiesWithoutResort > 0 && (
                <p className="text-xs text-amber-600">
                  {summary.propertiesWithoutResort} sem resort
                </p>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <Users className="h-4 w-4" />
                <span className="text-xs">Proprietários</span>
              </div>
              <p className="text-2xl font-bold">{summary.totalOwnersWithProperties}</p>
              <p className="text-xs text-muted-foreground">com imóveis cadastrados</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <TrendingUp className="h-4 w-4" />
                <span className="text-xs">Média geral</span>
              </div>
              <p className="text-lg font-bold">{formatCurrency(summary.overallAvgPricePerNight)}</p>
              <p className="text-xs text-muted-foreground">diária (por resort)</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <CalendarDays className="h-4 w-4" />
                <span className="text-xs">Com preço</span>
              </div>
              <p className="text-2xl font-bold">
                {resorts.reduce((s, r) => s + r.availabilityPeriodsCount, 0)}
              </p>
              <p className="text-xs text-muted-foreground">períodos ativos</p>
            </CardContent>
          </Card>
        </div>

        {unassigned.propertyCount > 0 && (
          <Card className="mb-6 border-amber-200 bg-amber-50/50 dark:bg-amber-950/20">
            <CardContent className="pt-6">
              <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                Imóveis sem resort vinculado: {unassigned.propertyCount} imóveis,{' '}
                {unassigned.ownerCount} proprietários
                {unassigned.avgPricePerNight != null && (
                  <> — média {formatCurrency(unassigned.avgPricePerNight)}</>
                )}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Por resort */}
        <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <Building2 className="h-5 w-5" />
          Detalhamento por resort
        </h2>
        {resorts.length === 0 ? (
          <Card>
            <CardContent className="p-6">
              <p className="text-muted-foreground">Nenhum empreendimento cadastrado.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {resorts.map((resort) => (
              <ResortCard key={resort.developmentId} resort={resort} />
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminResorts;
